import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { templateByKey } from './marketingOptions';
import { DesignCanvas } from './DesignCanvas';
import type { MarketingDesign } from '../../core/types/marketing';
type Props = {
  design: MarketingDesign;
  /** Maximale hoogte van de voorvertoning in px. */
  maxHeight?: number;
  /** Past het ontwerp in de hoogte én breedte van de ouder (die moet een hoogte hebben). */
  fill?: boolean;
  className?: string;
};

/**
 * Toont een ontwerp verkleind tot de beschikbare ruimte. Het ontwerp zelf blijft
 * op ware grootte (voor een scherpe download); alleen de weergave schaalt.
 *
 * Geen verspringing: de ruimte wordt vóór het eerste beeld gemeten (useLayoutEffect), en tot
 * die meting er is, houdt een rustig pulserend vlak in de juiste verhouding de plek vast.
 * Foto's in het ontwerp laden via ProductImage, die zelf ook pulseert tot ze er zijn.
 */
export const ScaledDesign = forwardRef<HTMLDivElement, Props>(function ScaledDesign({
  design,
  maxHeight,
  fill = false,
  className = ''
}, ref) {
  const t = templateByKey(design.template);
  const box = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState({
    w: 0,
    h: 0
  });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = (w: number, h: number) => setAvail(a => a.w === w && a.h === h ? a : {
      w,
      h
    });
    const r = el.getBoundingClientRect();
    measure(r.width, r.height);
    const ro = new ResizeObserver(([entry]) => measure(entry.contentRect.width, entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const limits = [avail.w / t.w];
  if (maxHeight) limits.push(maxHeight / t.h);
  if (fill && avail.h) limits.push(avail.h / t.h);
  const scale = avail.w ? Math.min(...limits) : 0;
  return <div ref={box} className={`flex w-full items-center justify-center ${fill ? 'h-full' : ''} ${className}`} data-id="element-3059" data-mp="sauaehvg7vf">
      {scale > 0 ? <div className="relative shrink-0 overflow-hidden shadow-canvas" style={{
      width: t.w * scale,
      height: t.h * scale
    }} data-id="element-3060" data-mp="4tw39orac20c">
          <div style={{
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        width: t.w,
        height: t.h
      }} aria-hidden="true" data-id="element-3061" data-mp="21rmioxfegpb9">
            <DesignCanvas ref={ref} design={design} data-id="element-3062" data-mp="2amu6a5ejexem" />
          </div>
        </div> : <div aria-hidden="true" className="max-h-full w-full bg-sand motion-safe:animate-pulse" style={{
      aspectRatio: `${t.w} / ${t.h}`,
      maxHeight
    }} data-id="element-3063" data-mp="25vu6mse1a18f" />}
    </div>;
});